import React from 'react';
import { Tooltip, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';
import type { CellRendererProps } from './cell-renderer-registry.js';
import { registerCellRenderer } from './cell-renderer-registry.js';
import { tableTokens } from '../table-tokens.ts';
import { useLocalizedFormatter } from '../../../../runtime/formatting/use-localized-formatter';

function ReferenceCellRenderer({ value, column, locale, referenceDisplayMap }: CellRendererProps) {
  const { t } = useTranslation();
  const referenceDescriptor = column.referenceDescriptor;
  if (!value || !referenceDescriptor) {
    return <Box component="span" sx={{ color: tableTokens.text.disabled }}>—</Box>;
  }

  const valueObj = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;
  const idValue = valueObj ? valueObj[referenceDescriptor.idField] : value;
  if (idValue === undefined || idValue === null || String(idValue).length === 0) {
    return <Box component="span" sx={{ color: tableTokens.text.disabled }}>—</Box>;
  }

  const labelValue = valueObj ? valueObj[referenceDescriptor.labelField] : undefined;
  const secondaryLabel = referenceDescriptor.secondaryLabelField && valueObj
    ? valueObj[referenceDescriptor.secondaryLabelField]
    : undefined;
  const entityType = referenceDescriptor?.entityType;

  const referenceMap = entityType ? referenceDisplayMap?.[entityType] : undefined;
  const resolved = referenceMap?.[String(idValue)];

  const resolvedLabel =
    (typeof resolved?.label === 'string' ? resolved.label : undefined) ??
    (typeof labelValue === 'string' ? labelValue : undefined) ??
    (labelValue !== undefined && labelValue !== null ? String(labelValue) : undefined);

  const displayLabel = resolvedLabel
    ?? (referenceDescriptor.unresolvedValueKey
      ? t(resolveTKey(referenceDescriptor.unresolvedValueKey))
      : '—');

  const displaySecondary =
    (resolved?.secondaryLabel !== undefined ? String(resolved.secondaryLabel) : null) ??
    (secondaryLabel !== null && secondaryLabel !== undefined ? String(secondaryLabel) : null) ??
    (resolvedLabel ? String(idValue) : null);

  const showTooltip = displayLabel.length > 30 || !!displaySecondary;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125 }}>
      {showTooltip ? (
        <Tooltip title={`${displayLabel}${displaySecondary ? `\n${displaySecondary}` : ''}`}>
          <Box component="span" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayLabel}
          </Box>
        </Tooltip>
      ) : (
        <Box component="span" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel}
        </Box>
      )}
      {displaySecondary && (
        <Box component="span" sx={{ fontSize: '0.75rem', color: tableTokens.text.secondary }}>
          {displaySecondary}
        </Box>
      )}
      {referenceDescriptor?.navigationDescriptor && (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
          {referenceDescriptor.navigationDescriptor.actions?.map((action) => (
            <span key={action.id}>{t(resolveTKey(action.labelKey))}</span>
          ))}
        </Box>
      )}
    </Box>
  );
}

registerCellRenderer({
  id: 'reference',
  labelKey: 'renderer.reference',
  component: React.memo(ReferenceCellRenderer),
  supportedTypes: ['reference'],
});

function CurrencyCellRenderer({ value, column, locale }: CellRendererProps) {
  const { formatByType } = useLocalizedFormatter();
  if (value === null || value === undefined) {
    return <span style={{ color: tableTokens.text.disabled }}>—</span>;
  }
  return <span>{formatByType(value, 'currency', { currencyCode: column.currency ?? 'USD' })}</span>;
}

registerCellRenderer({
  id: 'currency',
  labelKey: 'renderer.currency',
  component: React.memo(CurrencyCellRenderer),
  supportedTypes: ['money'],
});

function QuantityCellRenderer({ value, locale }: CellRendererProps) {
  const { formatByType } = useLocalizedFormatter();
  if (value === null || value === undefined) {
    return <span style={{ color: tableTokens.text.disabled }}>—</span>;
  }
  return <span>{formatByType(value, 'quantity')}</span>;
}

registerCellRenderer({
  id: 'quantity',
  labelKey: 'renderer.quantity',
  component: React.memo(QuantityCellRenderer),
  supportedTypes: ['quantity'],
});

function PercentageCellRenderer({ value, locale }: CellRendererProps) {
  const { formatByType } = useLocalizedFormatter();
  if (value === null || value === undefined) {
    return <span style={{ color: tableTokens.text.disabled }}>—</span>;
  }
  return <span>{formatByType(Number(value) / 100, 'percentage')}</span>;
}

registerCellRenderer({
  id: 'percentage',
  labelKey: 'renderer.percentage',
  component: React.memo(PercentageCellRenderer),
  supportedTypes: ['percentage'],
});

function DateTimeCellRenderer({ value, column, locale }: CellRendererProps) {
  const { formatByType } = useLocalizedFormatter();
  if (!value) return <span style={{ color: tableTokens.text.disabled }}>—</span>;
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return <span style={{ color: tableTokens.text.disabled }}>—</span>;
  }
  return <span>{formatByType(date, column.format === 'datetime' ? 'datetime' : 'date')}</span>;
}

registerCellRenderer({
  id: 'datetime',
  labelKey: 'renderer.datetime',
  component: React.memo(DateTimeCellRenderer),
  supportedTypes: ['date', 'datetime'],
});